import { useState, useContext, useEffect } from "react";
import {
    CustomButton,
    RegularButton,
} from "../../../common/ReusableComponent/CustomButton";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import ReusableModal from "../../AppPAges/ManageSettings/ReusableModal";
import { ReusableInput } from "../../../common/ReusableComponent/ReusableInput";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AddVendorModal from "../../AppPAges/ReusableForm/AddVendorModal";
import { useToastHandler } from "../../../common/ToastHandler";
import AssetTable from "../AssetsInventory/AssetTable";
import { capitalizeFirstLetter } from "../../../../utils/helper/common";
import useSwal from "../../../common/ReusableComponent/useSwal";
import { MdVerified } from "react-icons/md";
import AddMaintenanceCost from "../../../../assets/Image/maintenanceCost.png"
import StatusPill from "../../../common/ReusableComponent/StatusPill";
import ActionButton from "../../../common/ReusableComponent/ActionButton";
import { FaMoneyBillWave } from "react-icons/fa";

const MaintenanceCost = () => {
    const { user } = useContext(AuthContext);
    const { GetPaginatedData, Post, Patch, Delete } = useApi();
    const { handleToast } = useToastHandler();
    const { confirmDelete } = useSwal();
    const [showCostModal, setShowCostModal] = useState(false);
    const [maintenanceCostData, setMaintenanceCostData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [isCostEdit, setIsCostEdit] = useState(false);
    const [editingCostId, setEditingCostId] = useState(null);
    const [selectedMaintenanceLabel, setSelectedMaintenanceLabel] = useState("");
    const [selectedVendorId, setSelectedVendorId] = useState("");
    const [selectedVendorName, setSelectedVendorName] = useState("");
    const [refreshTable, setRefreshTable] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [costFormData, setCostFormData] = useState({
        maintenance_id: "",
        cost_name: "",
        description: "",
        total_cost: "",
        vendor_id: "",
        is_paid: false,
        paid_at: new Date().toISOString().split("T")[0],
    });

    const [costErrors, setCostErrors] = useState({
        cost_name: "",
        description: "",
        total_cost: "",
    });

    const [showPayModal, setShowPayModal] = useState(false);
    const [payingCost, setPayingCost] = useState(null);
    const [payForm, setPayForm] = useState({
        amount: "",
        paid_at: new Date().toISOString().split("T")[0],
        is_paid: true,
    });

    const handleCostChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === "cost_name" || name === "description") {
            processedValue = capitalizeFirstLetter(value);
        }

        setCostFormData({
            ...costFormData,
            [name]: processedValue,
        });

        if (costErrors[name]) {
            setCostErrors({
                ...costErrors,
                [name]: "",
            });
        }
    };

    const handleCostBlur = (e) => {
        const { name, value } = e.target;
        let error = "";

        if (name === "cost_name") {
            if (!value.trim()) {
                error = "Cost name is required";
            } else if (value.length > 100) {
                error = "Cost name must not exceed 100 characters";
            }
        }

        if (name === "description" && value && value.length > 500) {
            error = "Description must not exceed 500 characters";
        }

        if (name === "total_cost") {
            if (!value) {
                error = "Total cost is required";
            }
        }

        setCostErrors({
            ...costErrors,
            [name]: error,
        });
    };

    const resetMaintenanceCostForm = () => {

        setCostFormData({
            maintenance_id: "",
            cost_name: "",
            description: "",
            total_cost: "",
            vendor_id: "",
            is_paid: false,
            paid_at: new Date().toISOString().split("T")[0],
        });
        setCostErrors({
            cost_name: "",
            description: "",
            total_cost: "",
        });
        setIsCostEdit(false);
        setEditingCostId(null);
    };

    const fetchMaintenanceCost = async ({ page = 1, searchTerm = "" } = {}) => {
        try {
            setLoading(true);

            const response = await GetPaginatedData("assetMaintenanceCost", {
                page,
                search: searchTerm || undefined,
            });

            const results = response?.data || [];
            setMaintenanceCostData(results);
            setTotalCount(response?.totalCount || 0);

            return {
                data: results,
                totalPages: Math.ceil((response?.totalCount || 0) / 10),
                totalCount: response?.totalCount || 0,
                currentPage: page,
            };
        } catch {
            handleToast({
                type: "ERROR",
                message: "Failed to fetch maintenance cost records",
            });

            return {
                data: [],
                totalPages: 1,
                totalCount: 0,
                currentPage: page,
            };
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchMaintenanceCost({ page: currentPage, searchTerm: debouncedSearchTerm });
    }, [currentPage, debouncedSearchTerm, refreshTable]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const handleAddMaintenanceCost = async (e) => {
        e.preventDefault();

        const payload = {
            maintenance_id: costFormData.maintenance_id,
            store: user.store,
            cost_name: costFormData.cost_name,
            description: costFormData.description,
            total_cost: Number(costFormData.total_cost),
            vendor_id: costFormData.vendor_id || null,
            vendor_name: selectedVendorName || null,
            is_paid: costFormData.is_paid,
            paid_at: costFormData.is_paid
                ? costFormData.paid_at || new Date().toISOString()
                : null,
        };

        try {
            setLoading(true);

            if (isCostEdit) {
                await Patch("assetMaintenanceCost", editingCostId, payload);
                handleToast({ type: "SUCCESS", message: "Maintenance cost updated successfully" });
            } else {
                await Post("assetMaintenanceCost", payload);
                handleToast({ type: "SUCCESS", message: "Maintenance cost added successfully" });
            }

            setShowCostModal(false);
            resetMaintenanceCostForm();
            setRefreshTable(prev => prev + 1);
        } catch {
            handleToast({
                type: "ERROR",
                message: isCostEdit ? "Failed to update cost" : "Failed to add cost",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEditMaintenanceCost = (row) => {
        setIsCostEdit(true);
        setEditingCostId(row.id);

        setCostFormData({
            maintenance_id: row.maintenance_id,
            cost_name: row.cost_name,
            description: row.description,
            total_cost: row.total_cost,
            vendor_id: row.vendor_id,
            vendor_name: row.vendor_name,
            is_paid: row.is_paid,
            paid_at: row.paid_at ? row.paid_at.split("T")[0] : "",
        });

        setShowCostModal(true);
        setSelectedVendorId(row.vendor_id);
        setSelectedVendorName(row.vendor_name);
    };
    const handleDeleteMaintenanceCost = async (id) => {
        const result = await confirmDelete(
            "Are you sure?",
            "This maintenance cost will be permanently deleted"
        );

        if (!result.isConfirmed) return;

        try {
            setLoading(true);

            await Delete("assetMaintenanceCost", id);

            handleToast({
                type: "SUCCESS",
                message: "Maintenance cost deleted successfully",
            });

            setRefreshTable(prev => prev + 1);
        } catch {
            handleToast({
                type: "ERROR",
                message: "Failed to delete maintenance cost",
            });
        } finally {
            setLoading(false);
        }
    };
    const MAINTENANCE_COST_STATUS = {
        paid: {
            bg: "#e9fdf3",
            text: "#12b76a",
            border: "#a6f4c5",
        },
        unpaid: {
            bg: "#fdecec",
            text: "#e11d48",
            border: "#fda4af",
        },
    };

    const columns = [
        {
            header: "Asset",
            field: "asset_name",
        },
        {
            header: "Category",
            field: "category_name",
            render: (row) =>
                row?.category_name || "-",

        },

        {
            header: "Description",
            field: "description",
            render: (row) => row?.description || "-",
        },
        {
            header: "Total Cost",
            field: "total_cost",
            render: (row) => row?.total_cost || "-",
        },
        {
            header: "Cost Name",
            field: "cost_name",

        },

        {
            header: "Vendor",
            field: "vendor_name",
        },
        {
            header: "Paid At",
            field: "paid_at",
            render: (row) =>
                row?.paid_at
                    ? new Date(row.paid_at).toLocaleDateString()
                    : "-",
        },
        {
            header: "Status",
            render: (row) => {
                const status = row.is_paid ? "paid" : "unpaid";
                const cfg = MAINTENANCE_COST_STATUS[status];

                return (
                    <StatusPill
                        label={status}
                        bgColor={cfg.bg}
                        textColor={cfg.text}
                        borderColor={cfg.border}
                    />
                );
            },
        },
        {
            header: "Pay",
            render: (row) => {
                if (row.is_paid) {
                    return <MdVerified size={20} color="#12b76a" />;
                }

                return (
                    <ActionButton
                        onClick={() => {
                            setPayingCost(row);
                            setPayForm({
                                amount: row.total_cost,
                                paid_at: new Date().toISOString().split("T")[0],
                                is_paid: true,
                            });
                            setShowPayModal(true);
                        }}
                        bgColor="#ecfeff"
                        textColor="#0891b2"
                        borderColor="#bae6fd"
                        hoverBgColor="#cffafe"
                    >
                        <FaMoneyBillWave />
                        Pay ₹{row.total_cost}
                    </ActionButton>
                );
            },
        },

    ];

    return (
        <div className="responsive-box">
            <div className="inventory-header">
                <div className="inventory-title">
                    Assets Maintenance Cost
                </div>
                <div
                    className="btn-position"
                    style={{ display: "flex", gap: "10px" }}
                >
                    <CustomButton
                        text="Add Maintenance Cost"
                        icon={AddMaintenanceCost}
                        onClick={() => {
                            resetMaintenanceCostForm();
                            setShowCostModal(true);
                        }}
                        color="#000000"
                        backgroundColor="#FFAF32"
                    />
                </div>
            </div>
            <ReusableModal
                title={isCostEdit ? "Edit Maintenance Cost" : "Add Maintenance Cost"}
                show={showCostModal}
                handleClose={() => {
                    setShowCostModal(false);
                    resetMaintenanceCostForm();
                }}
                width="500px"
            >
                <form onSubmit={handleAddMaintenanceCost}>
                    <div className="d-flex align-items-center mx-3">
                        <PaginatedSearchSelect
                            api="assetMaintenance"
                            GetPaginatedData={GetPaginatedData}
                            style={{ width: "500px" }}
                            getOptionLabel={(opt) => opt?.asset_name || ""}
                            onSelect={(opt) => {
                                setCostFormData(prev => ({
                                    ...prev,
                                    maintenance_id: opt?.id || "",
                                }));
                                setSelectedMaintenanceLabel(opt?.asset_name || "");
                            }}
                            placeholder="Select Maintenance"
                            pageSize={10}
                            filterFn={(item) =>
                                ["approved", "in_progress"].includes(item.status) &&
                                !item.deleted_at
                            }
                            value={
                                costFormData.maintenance_id
                                    ? {
                                        id: costFormData.maintenance_id,
                                        asset_name: selectedMaintenanceLabel,
                                    }
                                    : null
                            }
                        />

                    </div>
                    <div className="d-flex align-items-start mb-3 mx-3 mt-4">
                        <ReusableInput
                            name="cost_name"
                            label="Cost Name"
                            value={costFormData.cost_name}
                            onChange={handleCostChange}
                            onBlur={handleCostBlur}
                            maxLength={100}
                            error={costErrors.cost_name}
                            required
                        />
                    </div>
                    <div className="d-flex align-items-start mb-3 mx-3">
                        <ReusableInput
                            name="description"
                            label="Description"
                            value={costFormData.description}
                            onChange={handleCostChange}
                            onBlur={handleCostBlur}
                            maxLength={500}
                            error={costErrors.description}
                        />
                    </div>
                    <div className="d-flex align-items-start mb-3 mx-3">
                        <ReusableInput
                            type="number"
                            name="total_cost"
                            label="Total Cost"
                            value={costFormData.total_cost}
                            onChange={handleCostChange}
                            onBlur={handleCostBlur}
                            error={costErrors.total_cost}
                            required
                        />
                    </div>

                    <div className="d-flex align-items-start mb-3 mx-3">
                        <PaginatedSearchSelect
                            api="vendorDepartmentData"
                            GetPaginatedData={GetPaginatedData}
                            style={{ width: "500px" }}
                            getOptionLabel={(opt) => opt?.name || ""}
                            onSelect={(opt) => {
                                if (opt?.id === "add-vendor") {
                                    setShowVendorModal(true);
                                } else {
                                    setSelectedVendorId(opt?.id || "");
                                    setSelectedVendorName(opt?.name || "");
                                    setCostFormData(prev => ({
                                        ...prev,
                                        vendor_id: opt?.id || "",
                                    }));
                                }
                            }}
                            placeholder="Select Vendor"
                            pageSize={10}
                            value={
                                selectedVendorId
                                    ? { id: selectedVendorId, name: selectedVendorName }
                                    : null
                            }
                            filterFn={(vendor) =>
                                vendor.type === "purchase" && !vendor.is_deleted
                            }
                            extraOptions={[{ id: "add-vendor", label: "Add Vendor" }]}
                        />
                    </div>
                    <div className="d-flex mb-3 mx-3">
                        <input
                            type="checkbox"
                            checked={costFormData.is_paid}
                            onChange={(e) =>
                                setCostFormData(prev => ({ ...prev, is_paid: e.target.checked }))
                            }
                            style={{
                                transform: "scale(1.5)",
                                accentColor: "black",
                            }}
                        />
                        <label className="form-check-label" htmlFor="is_active">
                            Paid
                        </label>

                    </div>

                    {costFormData.is_paid && (
                        <div className="d-flex align-items-start mb-3 mx-3 mt-4">
                            <ReusableInput
                                type="date"
                                label="Paid At"
                                value={costFormData.paid_at}
                                onChange={(e) =>
                                    setCostFormData(prev => ({ ...prev, paid_at: e.target.value }))
                                }
                            />
                        </div>
                    )}

                    <div className="d-flex justify-content-end mt-3">
                        <RegularButton
                            type="submit"
                            text={isCostEdit ? "Update" : "Save"}
                            backgroundColor="#282e26"
                        />
                    </div>
                </form>
            </ReusableModal>
            <ReusableModal
                title="Pay Maintenance Cost"
                show={showPayModal}
                handleClose={() => setShowPayModal(false)}
                width="400px"
            >
                <div className="p-3">
                    <div className="p-2">
                        <ReusableInput
                            type="number"
                            label="Amount"
                            value={payForm.amount}
                            onChange={(e) =>
                                setPayForm(prev => ({ ...prev, amount: e.target.value }))
                            }
                            required
                            readOnly
                        />
                    </div>
                    <div className="p-2">
                        <ReusableInput
                            type="date"
                            label="Paid At"
                            value={payForm.paid_at}
                            onChange={(e) =>
                                setPayForm(prev => ({ ...prev, paid_at: e.target.value }))
                            }
                            required
                        />
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-2 p-3">
                        <input
                            type="checkbox"
                            checked={payForm.is_paid}
                            disabled
                            onChange={(e) =>
                                setPayForm(prev => ({ ...prev, is_paid: e.target.checked }))
                            }
                            style={{
                                transform: "scale(1.5)",
                                accentColor: "black",
                            }}
                        />
                        <label>Paid</label>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        <RegularButton
                            text="Submit"
                            backgroundColor="#282e26"
                            onClick={async () => {
                                try {
                                    setLoading(true);

                                    await Patch("assetMaintenanceCost", payingCost.id, {
                                        total_cost: Number(payForm.amount),
                                        is_paid: true,
                                        paid_at: payForm.paid_at || new Date().toISOString(),
                                    });

                                    handleToast({
                                        type: "SUCCESS",
                                        message: "Payment recorded successfully",
                                    });

                                    setShowPayModal(false);
                                    setRefreshTable(prev => prev + 1);
                                } catch {
                                    handleToast({
                                        type: "ERROR",
                                        message: "Failed to record payment",
                                    });
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        />
                    </div>
                </div>
            </ReusableModal>
            <AssetTable
                data={maintenanceCostData}
                columns={columns}
                loading={loading}
                showAction={true}
                onEdit={handleEditMaintenanceCost}
                onDelete={handleDeleteMaintenanceCost}
                showFooter
                currentPage={currentPage}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                searchTerm={searchTerm}
                onSearch={handleSearch}
            />
            {showVendorModal && (
                <AddVendorModal
                    show={showVendorModal}
                    handleClose={() =>
                        setShowVendorModal(false)
                    }
                />
            )}
        </div>

    )
}

export default MaintenanceCost
